import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { PresentContext } from './presentContext';
import { playMotion, snapshotMotion, type MotionSnapshot } from './presenceMotion';
import styles from './Presence.module.css';

export function Presence({ show, children }: { show: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(show);
  const root = useRef<HTMLDivElement>(null);
  const interrupted = useRef<MotionSnapshot | undefined>(undefined);
  if (show && !mounted) setMounted(true);

  useLayoutEffect(() => {
    const node = root.current;
    if (node === null) return undefined;
    const animations = playMotion(node, show ? 'in' : 'out', interrupted.current);
    interrupted.current = undefined;
    if (!show && animations.length === 0) {
      setMounted(false);
      return undefined;
    }
    if (!show) void Promise.all(animations.map((a) => a.finished)).then(() => { interrupted.current = undefined; setMounted(false); }, () => undefined);
    return () => {
      if (animations.some((animation) => animation.playState === 'running')) interrupted.current = snapshotMotion(node);
      for (const animation of animations) animation.cancel();
    };
  }, [show, mounted]);

  if (!mounted) return null;
  return (
    <div ref={root} className={styles.presence} inert={!show}>
      <PresentContext.Provider value={show}>{children}</PresentContext.Provider>
    </div>
  );
}
